/**
 * Staff Assignments Tab Component
 * Displays and manages staff assignments for a report (proposal)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, EmptyState } from '../UI';
import { useToastStore } from '../../stores/toast';
import { useWorkspace } from '../../hooks/useWorkspace';
import {
  User,
  UserPlus,
  X,
  Edit,
  Trash2,
  Clock,
  Award,
  Building2,
  AlertTriangle,
} from 'lucide-react';

interface StaffAssignment {
  id: string;
  staff_member_id: string;
  staff_name?: string;
  staff_designations?: string[];
  staff_department?: string;
  role?: string;
  assigned_hours_per_week?: number;
  assigned_at: string;
  assigned_by?: string;
  status: 'active' | 'completed' | 'cancelled';
  notes?: string;
  assignment_metadata?: Record<string, any>;
}

interface StaffAssignmentsTabProps {
  reportId: string;
  report?: any;
}

export function StaffAssignmentsTab({ reportId, report }: StaffAssignmentsTabProps) {
  const { addToast } = useToastStore();
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<StaffAssignment | null>(null);

  // Fetch staff assignments
  const { data: assignmentsData, isLoading, refetch } = useQuery({
    queryKey: ['report-staff-assignments', reportId],
    queryFn: async () => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      const response = await api.get(`/api/rfp/staff/report-assignments/${reportId}`);
      return response;
    },
    enabled: !!reportId,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const assignments = assignmentsData?.assignments || [];

  // Remove assignment mutation
  const removeMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const { getAPIClient } = await import('../../api/client');
      const api = getAPIClient();
      try {
        await api.delete(`/api/rfp/staff/report-assignments/${assignmentId}`);
      } catch (error: any) {
        throw new Error(error.response?.data?.detail || 'Failed to remove assignment');
      }
    },
    onSuccess: () => {
      // Invalidate all assignment queries to refresh counts everywhere
      queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
      addToast({ message: 'Staff assignment removed', type: 'success' });
      refetch();
    },
    onError: (error: any) => {
      addToast({
        message: error.response?.data?.detail || 'Failed to remove assignment',
        type: 'error',
      });
    },
  });

  const handleRemove = (assignmentId: string) => {
    if (confirm('Remove this staff assignment?')) {
      removeMutation.mutate(assignmentId);
    }
  };

  // Dynamically import StaffAssignmentModal from plugin
  const [StaffAssignmentModal, setStaffAssignmentModal] = useState<React.ComponentType<any> | null>(null);

  React.useEffect(() => {
    const loadModal = async () => {
      try {
        // Try relative path first (works during build)
        try {
          // @ts-ignore - Plugin path may not be available at compile time
          const module = await import('../../../../plugins/rfp-agent/src/components/StaffAssignmentModal');
          if (module && module.StaffAssignmentModal) {
            setStaffAssignmentModal(() => module.StaffAssignmentModal);
            return;
          }
        } catch (e) {
          console.debug('StaffAssignmentModal import failed (relative path):', e);
        }
        
        // Fallback: try alias path (only works at runtime when alias is configured)
        try {
          // Construct path dynamically to prevent Rollup from analyzing it
          // @ts-ignore - Dynamic import path
          const pluginPath = '@tracekit' + '/plugin-rfp-agent/components/StaffAssignmentModal';
          const module = await import(/* @vite-ignore */ pluginPath);
          if (module && module.StaffAssignmentModal) {
            setStaffAssignmentModal(() => module.StaffAssignmentModal);
            return;
          }
        } catch (e) {
          console.debug('StaffAssignmentModal import failed (alias path):', e);
        }
      } catch (error) {
        console.warn('StaffAssignmentModal not available:', error);
      }
    };
    
    loadModal();
  }, []);

  if (isLoading) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Loading staff assignments...</span>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <User className="w-5 h-5" />
            Staff Assignments
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {assignments.length} staff member{assignments.length !== 1 ? 's' : ''} assigned to this proposal
          </p>
        </div>
        {StaffAssignmentModal && (
          <Button
            variant="primary"
            onClick={() => setShowAssignModal(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Assign Staff
          </Button>
        )}
      </div>

      {/* Assignments List */}
      {assignments.length === 0 ? (
        <Card className="p-8">
          <EmptyState
            icon={<User className="w-12 h-12" />}
            title="No Staff Assigned"
            description="Assign staff members to this proposal to track workload and capacity"
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {assignments.map((assignment: StaffAssignment) => (
            <Card key={assignment.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {assignment.staff_name || 'Unknown Staff'}
                    </span>
                    {assignment.role && (
                      <Badge variant="outline" className="text-xs">
                        {assignment.role}
                      </Badge>
                    )}
                    <Badge
                      variant={assignment.status === 'active' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {assignment.status}
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                    {assignment.staff_department && (
                      <span className="flex items-center gap-1">
                        <Building2 className="w-3 h-3" />
                        {assignment.staff_department}
                      </span>
                    )}
                    {assignment.staff_designations && assignment.staff_designations.length > 0 && (
                      <span className="flex items-center gap-1">
                        <Award className="w-3 h-3" />
                        {assignment.staff_designations.join(', ')}
                      </span>
                    )}
                    {assignment.assigned_hours_per_week && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {assignment.assigned_hours_per_week}h/week
                      </span>
                    )}
                    {assignment.assigned_at && (
                      <span className="text-xs">
                        Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  {assignment.notes && (
                    <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Notes: </span>
                      {assignment.notes}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemove(assignment.id)}
                    disabled={removeMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Staff Modal */}
      {StaffAssignmentModal && showAssignModal && (
        <StaffAssignmentModal
          reportId={reportId}
          reportName={report?.name || report?.client_name}
          projectId={projectId || ''}
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            refetch();
            // Also invalidate other assignment queries to refresh counts in other tabs
            queryClient.invalidateQueries({ queryKey: ['all-report-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['report-staff-assignments'] });
            queryClient.invalidateQueries({ queryKey: ['staff-capacity'] });
            queryClient.invalidateQueries({ queryKey: ['staff-availability'] });
          }}
        />
      )}
    </div>
  );
}
